import { useContext, useState } from 'react';

import { EditorSectionSetterContext } from './editorSectionContext';

export interface EditorSectionControls<T extends string> {
  defaultSection: T;
  sections: readonly T[];
  /** Set only without a routed provider (e.g. in tests): then the editor owns the section. */
  localSection: T | null;
  setSection: (next: string) => void;
}

/**
 * Tab controls for an editor whose section lives in the URL. The editor does not subscribe to
 * the section, so switching tabs re-renders only `EditorSectionTabs`, never the form and its
 * panels. The setter (e.g. to open the first invalid section) is stable.
 */
export function useEditorSectionControls<T extends string>(
  defaultSection: T,
  sections: readonly T[],
) {
  const routedSetter = useContext(EditorSectionSetterContext);
  const [localSection, setLocalSection] = useState(defaultSection);
  const setSection = (next: string) => {
    if (!sections.some((section) => section === next)) return;
    if (routedSetter) routedSetter(next);
    else setLocalSection(next as T);
  };
  const controls: EditorSectionControls<T> = {
    defaultSection,
    sections,
    localSection: routedSetter ? null : localSection,
    setSection,
  };
  return [controls, setSection as (next: T) => void] as const;
}
